/**
 * useContentPort — port-factory hook returning a stable
 * {@link ContentReaderPort} adapter for production composition (TA15).
 *
 * The composer (`main.tsx` / `Composition`) calls this hook once at
 * the root to obtain a `ContentReaderPort` instance and passes it
 * through `PortsProvider` as the `content` slot. Test harnesses
 * (`renderAppWithPorts`) deliberately do NOT call this factory — the
 * harness leaves the `content` slot omitted by default so the
 * Cluster 1 missing-port stub semantics (`useContent()` throws when
 * the slot is absent) are preserved across the entire test suite.
 * Tests that need a real adapter pass one explicitly via
 * `renderAppWithPorts({ content })`.
 *
 * Mechanism: `useState(() => new TrpcContentReader(...))[0]`. The lazy
 * initialiser runs exactly once per mount; the tuple's first element
 * is a stable reference for the component's lifetime. The setter is
 * intentionally discarded — the adapter holds no React state, the
 * tRPC client owns its own internals, and the consumer hook layer
 * (TA16's `useCategories` / `useArticle` / `useCuratedCards`) wraps
 * the port in React Query for caching + loading semantics per D-046.
 *
 * Why a hook and not a module-scope const
 * =======================================
 *
 * Two reasons mirror the rationale on `useSessionPort` /
 * `useLanguagePort`:
 *
 *   1. Per-React-root freshness — re-mounting (e.g. test reload
 *      simulation) constructs a fresh adapter rather than reusing
 *      a module-scoped singleton that may carry stale connection
 *      state.
 *   2. Composition discipline — the composer pattern is uniform
 *      across all ports: each port has a `useXyzPort` factory hook
 *      colocated under `apps/web/src/providers/`, and the composer
 *      calls each in sequence to build the `Ports` aggregate.
 *
 * Authentication
 * ==============
 *
 * Content reads use the API's `sessionProcedure`. The BFF session
 * cookie rides the same-origin request automatically, so the factory
 * builds the tRPC client without an `Authorization` header; the admin
 * client (`useAdminTrpcClient` in `apps/web/src/components/cms/hooks/
 * adminTrpcContext.tsx`) authenticates the same way.
 *
 * Replacement path
 * ================
 *
 * If a future feature introduces an alternative content source (a
 * fixture-backed adapter for offline preview, an indexed-search
 * adapter, etc.), the swap is a one-line change in this file or a
 * sibling factory hook — `useContent()` consumers and `PortsProvider`
 * are unchanged.
 */

import { useState } from 'react';

import { TrpcContentReader } from '../content/adapters/TrpcContentReader.js';
import type { ContentReaderPort } from '../content/ContentReaderPort.js';
import { createWebTrpcClient } from '../trpc/client.js';

export function useContentPort(): ContentReaderPort {
  const [port] = useState<ContentReaderPort>(() => new TrpcContentReader(createWebTrpcClient()));
  return port;
}
